import { z } from 'zod';
import { onuStateSchema } from './olt.js';
import { SSID_BAND_PORTS, bandForSsidPort, isNextFreeSsidPort, ssidIndexOf, type SsidBand } from './wifi-band.js';
import { onuManagementStateSchema } from './onu-management.js';
import { wanIpv6Schema } from './wan-ipv6.js';

/**
 * Bulk actions on ONUs — the most dangerous surface this platform has.
 *
 * The shape here encodes the operator's rule rather than describing it. A bulk
 * request is TWO calls, never one:
 *
 *   1. POST a filter → a PLAN. The plan freezes the exact ONUs that matched,
 *      what each one would receive, and which would be refused and why. It
 *      writes nothing.
 *   2. POST that plan's id, with the count confirmed back → an EXECUTION.
 *      The write set comes from the plan's frozen rows, never from re-running
 *      the filter, because the filter can match more ONUs at 02:05 than it did at
 *      02:00 and "the list grew while the operator was reading it" is a fleet
 *      outage with an audit trail that says it was authorised.
 *
 * There is no single-call form and there must never be one. An endpoint that
 * takes a filter and a "confirm: true" is the whole safety design deleted in
 * one commit — the same reason `applyDevicePresetSchema` refuses an array of
 * targets.
 */

/**
 * What a bulk run can do.
 *
 * Every entry is a write this platform performs today on BOTH vendors, or says
 * plainly which one it cannot. Actions the operator asked for that no verified
 * write path exists for are deliberately absent rather than present and
 * silently inert — see `docs/vendor-support.md` for what each chassis answers.
 *
 * "Silently inert" is the thing that is banned, not "unavailable". An action
 * whose write path is built but not yet exercised on a given chassis is
 * PRESENT and REFUSES on that chassis, naming the row of `vendor-support.md`
 * that would lift the refusal — because an operator who cannot see the control
 * cannot see why it is missing either. The gate lives in one table
 * (`services/bulk/vendor-support.ts`) so raising a chassis from ⚠️ to ✅ is one
 * line in the same commit as the doc, and so it can never be applied to one
 * vendor and forgotten on the other.
 */
export const bulkActionIdSchema = z.enum([
  /** A device config preset: WiFi, LAN, WAN (mode, VLAN, DNS) — TR-069 or OMCI. */
  'device_preset',
  /** TR-069 ↔ OMCI, the ONU's own management address and management VLAN. */
  'management_mode',
  /** Which TR-069 profile (which ACS) this ONU is pointed at. */
  'tr069_profile',
  /** Attach a subscriber VLAN, bridged or routed, and push the service config. */
  'attach_vlan',
  /**
   * Flip an already-attached VLAN between bridged and routed — the OLT half
   * alone (the service config re-rendered).
   *
   * KEPT, and no longer OFFERED (2026-09-14). The picker now shows `wan_mode`
   * where "Routed / bridged" was, because the operator asked for every option
   * of the ONU page's WAN dialog in bulk and this id carried one of them. It
   * stays in the enum, the ladder and both `actions.ts` switches because it is
   * in the ledger: `bulk_plans.action` holds it on every run that ever used
   * it, `GET /plans/:id` parses those rows through `bulkPlanSchema`, and
   * "Finish the rest" re-plans from a stored action. An id removed from here
   * is a history page that throws on its own past.
   */
  'vlan_mode',
  /**
   * The WAN dialog's Apply, per ONU — VLAN → Mode → Addressing → IP protocol
   * → Firewall, then the same writes in the same order the dialog makes them:
   * the VLAN row, the service config, the WAN object (chassis `wan-ip` on a
   * ZTE ONU, `configureWan` over TR-069 on a Huawei ONT), the firewall in the
   * same transcript, the IPv6 half to the CPE.
   *
   * Its own id rather than options on `vlan_mode`, because `vlan_mode` is the
   * OLT half alone and this also writes the WAN object, the firewall and the
   * IPv6 half; a ledger row saying `vlan_mode` about a run that re-stated 400
   * PPPoE WANs is a lie in the one record that outlives the ONUs — the same
   * reason `vlan_change` and `hotspot` are their own ids.
   *
   * What it deliberately does NOT carry: a password, an address, a username.
   * A PPPoE account and a static address are per subscriber by definition,
   * and one value across a batch is two ONUs fighting over an address — the
   * same reason `management_mode` refuses a static management address in
   * bulk. The request says "as recorded on each ONU" and the resolver refuses
   * BY NAME an ONU whose row records no complete account or address.
   */
  'wan_mode',
  /**
   * MOVE a subscriber from one VLAN to another.
   *
   * Its own action rather than an option on `attach_vlan`, and the reason is
   * that its preconditions are the inverse: `attach_vlan` REFUSES an ONU that
   * already carries the VLAN, a move refuses one that does not carry the old
   * one. Folding them would give a single action whose blockers flip meaning
   * on an optional field — the discriminated union exists precisely so that
   * "this action needs these settings" is a parse error at the boundary rather
   * than a runtime branch. It also has to read differently in the ledger a year
   * later: a run recorded as `attach_vlan` that actually moved 400 subscribers
   * off VLAN 20 is a lie in the only record that outlives the ONUs.
   */
  'vlan_change',
  /**
   * Put an SSID on a band — a name, and WPA/WPA2-PSK with a key or nothing.
   *
   * Addresses a BAND, never an interface number, because `wifi_0/5` is not a
   * 5 GHz interface on two thirds of this fleet; `ssidPortForBand` resolves the
   * band against the ONU's own model and REFUSES a band the hardware has not
   * got rather than moving the name to the other radio.
   */
  'ssid_set',
  /**
   * Take an SSID back off, matched by NAME.
   *
   * Its own id rather than a mode on `ssid_set`, for the reason `vlan_change`
   * is its own id and then some. Its preconditions are the inverse — a set
   * refuses an ONU whose model has no interface on the band, a remove refuses
   * an ONU that is not carrying the name — and unlike the VLAN pair the two
   * do not even use the same WRITE PATH: a set is chassis commands on a ZTE
   * OLT, a remove is a TR-069 parameter on the CPE. One id would put two
   * opposite vendor stories in one row of `vendor-support.ts`, and would leave
   * the ledger saying `ssid` a year later about the run that took a hotspot
   * off two thousand subscribers.
   */
  'ssid_remove',
  /**
   * A PUBLIC HOTSPOT on a set of ZTE OMCI-managed ONUs: one bridged
   * subscriber VLAN plus one public SSID on a secondary interface of each
   * radio (`wifi_0/2` and `wifi_0/6` by the operator's convention), bound to
   * that VLAN.
   *
   * Its own id rather than an `attach_vlan` followed by an `ssid_set`, because
   * neither of those on its own is the thing the operator asked for and the
   * ledger has to say what happened: a run recorded as `ssid_set` that also
   * put 400 subscribers on VLAN 300 is a lie in the one record that outlives
   * the ONUs. It also has to be one CHASSIS SESSION per ONU — the VLAN, the
   * SSID interfaces' binding to it and the SSID names and keys — because a
   * public network that exists on the radio before it exists on the VLAN is a
   * public network that hands out nothing.
   *
   * `enabled: false` HIDES the two SSIDs (`ssid ctrl <port> hide enable`, the
   * only captured way to stop a ZTE SSID being offered) and does not rename or
   * re-key them. The VLAN half of a disable is the same removal `vlan_change`
   * waits on, and refuses for the same one reason.
   */
  'hotspot',
  /** Placement zone. Platform bookkeeping — no chassis is touched. */
  'zone',
  /** Mass firmware upgrade, as a campaign. Created, never started. */
  'firmware_campaign',
  /**
   * Re-STATE SimpleACS's stored VLANs and ports onto each ONU. Nothing removed.
   *
   * This is a WRITE, and the name is the platform's own: the ONU page's Resync
   * button sends exactly these commands. The READ half of that page is Summon,
   * and it is deliberately not offered here — every ONU on every chassis is
   * already re-read by the hourly fleet sweep, and any ONU somebody is looking
   * at is re-read every fifteen seconds by the page itself. A bulk button for
   * it would be a manual re-trigger of a job that already runs, which is the
   * exact shape of control this feature refuses to ship.
   */
  'onu_resync',
  /**
   * Read each ONU's configuration off the chassis and RECORD what is missing
   * — VLAN intent, socket rows, the omci-config snapshot the tabs read.
   * Writes nothing to any chassis or CPE; the only writes are this
   * platform's own rows, and only where it holds nothing for that object.
   * The first action on the read rung, and exactly what that rung was kept
   * for: the per-ONU config read is NOT covered by the hourly sweep (that is
   * inventory), so this is not a re-trigger of a job that already runs.
   */
  'onu_adopt',
  /**
   * Wake each ONU's CPE over TR-069: a connection request plus the common
   * parameter refresh — WiFi states, WAN, addresses. The refusal that used to
   * live here ("Summon is a re-trigger of a job that already runs") conflated
   * two different reads: the hourly sweep re-reads INVENTORY off the chassis,
   * while a summon refreshes the CPE's OWN tree, which otherwise waits for
   * the nightly 1–5 am batch. After the ACS wipe left a whole fleet of
   * never-fully-read trees, the operator asked for exactly this.
   */
  'onu_summon',
  /**
   * Whether each ONU answers unsolicited traffic from the WAN side.
   *
   * A ZTE `pon-onu-mng` verb, and one whose success depends on the ONU's MODEL
   * rather than on the chassis: `firewall ?` lists `enable` for every unit on a
   * C300, and a Huawei-made HG8546M then answers `unknown ME` because it has no
   * such OMCI object. This fleet carries 2,165 of them, so on a whole-chassis
   * run most rows will report exactly that — as a per-ONU outcome naming the
   * model, never as a failure of the run.
   *
   * It now carries a SCOPE, and the scope's default is what it always did —
   * see `bulkFirewallScopeSchema`. The whole-ONU flag stays reachable and
   * unchanged, because an operator depends on it today.
   */
  'onu_firewall',
  /**
   * The subscriber WAN's IP protocol ALONE — dual stack with its two modes, or
   * IPv4 only — and nothing else touched.
   *
   * `wan_mode` carries the same step, but it re-states the whole WAN with it:
   * VLAN, routed/bridged, addressing, firewall. Turning prefix delegation on
   * across a fleet through that action means re-writing every subscriber's
   * PPPoE account and VLAN to change one leaf, which is a far bigger write
   * than the operator asked for (2026-09-23: "add to bulk actions, ipv6 pd
   * with ipv6 set on none"). This action writes the IPv6 leaves and nothing
   * else — the same `applyWanIpv6` the WAN dialog uses, judged per ONU by the
   * same `ipv6Refusals` gate — and records the intent on the ONU's routed
   * internet row so a CPE that has not linked yet gets it at first link.
   */
  'wan_ipv6',
  /**
   * Each router's own clock onto the platform's: East Africa Time, kept by
   * NTP from the org's time servers (operator, 2026-10-05: "build the router
   * clocks"). The fleet's Huawei routers shipped with NTP off and GMT, the
   * office F670L with no time server at all — their clocks read 1981 and
   * 1970. Written in each family's own spelling (`services/devices/
   * router-clock.ts`), only where it differs; a router already on EAT and
   * synchronised is left alone. No setting: the zone is the platform's.
   */
  'router_clock',
  /**
   * Move subscribers onto a speed profile the CHASSIS ALREADY HAS.
   *
   * Not "set the speed", and the difference is 2,120 service-ports wide. ZTE's
   * `traffic-profile` and `profile tcont` verbs are create-or-replace and
   * silent, so one edit of `SMARTOLT-1G-DOWN` re-rates every subscriber bound
   * to it; the same is true of a Huawei traffic table and truer of a line
   * profile (597 ONTs share profile 7 on the X17). So this action BINDS and
   * never writes a profile: `services/olt/speed-rebind.ts` is built so that
   * editing one is not expressible — no renderer there takes a rate, and every
   * rendered line is matched against a closed allowlist of binding shapes
   * before a plan can leave the module.
   *
   * The bulk request therefore names PROFILES, never kilobits. That is a
   * stricter promise than the module's own (`WantedProfile.maxKbps` exists so
   * that a single-ONU caller can ask "put them on 100 megs" and have a profile
   * LOOKED UP), and it is kept stricter on purpose: a bulk request is stored,
   * re-read hours later and executed by a different code path, and a number on
   * it is one refactor away from being the number somebody writes a profile
   * with.
   */
  'speed_rebind',
  /** Reboot each ONU through the chassis. A minute of lost service, each. */
  'onu_reboot',
  /** Deauthorise each ONU and build it again from what SimpleACS holds. */
  'onu_recreate',
  /**
   * Move every rate binding an ONU carries off one set of profile NAMES onto
   * another the chassis already has — per kind, T-CONT or traffic (operator,
   * 2026-09-26: "replace SmartOLT with SimpleACS related settings, even speed
   * profiles … assign all ONUs/Ports 1G/M1000").
   *
   * By name rather than by VLAN or role, because that is what the job is: every
   * `SMARTOLT-1G-UP` T-CONT becomes `M1000-UP` whatever it carries, and the
   * management T-CONT is renamed by the same pair list as the data one. Binds
   * only, like `speed_rebind` — every line passes the same closed allowlist, so
   * no profile is ever created or edited by it. C300 first; the C650's
   * service-port and QoS-policy lines follow once confirmed on a test ONU.
   */
  'profile_migrate',
]);
export type BulkActionId = z.infer<typeof bulkActionIdSchema>;

/**
 * How much a bulk action costs the subscriber if it is wrong — the ladder the
 * confirmation is built on.
 *
 * These are four different kinds of thing and the screen must not flatten them.
 * The dialog in front of 2,000 reboots cannot look like the one in front of
 * 2,000 resyncs, so the ladder is data rather than a judgement made in a
 * component: the server reads the same table and refuses the same way.
 *
 *   read     nothing is written anywhere. Worst case is a busy chassis.
 *   write    configuration changes. A wrong one is fixed by another write.
 *   service  the subscriber's connection drops or their management path moves.
 *   rebuild  the ONU is deauthorised and re-provisioned. On the wrong unit
 *            that is an outage plus a re-provision, not a mistake to undo.
 *
 * `read` finally has its tenant — `onu_adopt` — and stays what it always
 * was: the rung the ladder is measured from — the mildest thing a bulk run can ask
 * — and leaving it out would mean the mildest thing that exists (a zone change,
 * which touches no hardware at all) silently became the floor.
 */
export const bulkActionRiskSchema = z.enum(['read', 'write', 'service', 'rebuild']);
export type BulkActionRisk = z.infer<typeof bulkActionRiskSchema>;

export const BULK_ACTION_RISK: Record<BulkActionId, BulkActionRisk> = {
  zone: 'write',
  tr069_profile: 'write',
  device_preset: 'write',
  attach_vlan: 'write',
  onu_resync: 'write',
  /** Chassis reads and platform-row writes only — the rung's first tenant. */
  onu_adopt: 'read',
  /** Connection request + refresh tasks. Nothing configured anywhere. */
  onu_summon: 'read',
  /**
   * `write`, not `service`: no connection drops either way. Turning it OFF
   * exposes a subscriber's router to the WAN, which is why the form says so in
   * as many words — but it is undone by the opposite run, which is exactly what
   * this rung of the ladder means.
   */
  onu_firewall: 'write',
  /**
   * A write, not a service change: the IPv6 leaves are written on their own and
   * the IPv4 half of the connection — the address the subscriber is actually
   * using — is not touched by any of them.
   */
  wan_ipv6: 'write',
  /** A clock and its time servers: no connection, VLAN or address is touched. */
  router_clock: 'write',
  firmware_campaign: 'write',
  /**
   * `service`, and the rung is decided by the WORST vendor rather than the
   * commonest one.
   *
   * On ZTE nothing is expected to drop: the binding is replaced on an interface
   * that stays up. On Huawei there is no captured in-place rewrite of a
   * service-port's traffic-table indices — `service-port <index> inbound
   * traffic-table index N outbound traffic-table index M` is a CANDIDATE nobody
   * has run — so the only strategy composed entirely of grammar this fleet
   * already runs is `undo service-port <n>` followed by the recreate, and
   * between those two commands the subscriber's VLAN has no forwarding path.
   *
   * One action, one rung, and it has to be the Huawei one. `BULK_ACTION_RISK`
   * is keyed on the action id and is read by the browser BEFORE a chassis is
   * chosen, so a `write` rung here would put the mildest dialog in front of a
   * run that drops every subscriber it touches on two of this fleet's four
   * chassis. Over-asking on ZTE costs a typed word; under-asking on Huawei
   * costs the connection.
   */
  speed_rebind: 'service',
  /**
   * Management mode is `service`, not `write`, and that is a deliberate
   * promotion of an action that already shipped. It rewrites the path this
   * platform reaches the ONU on; getting it wrong across a chassis does not
   * degrade a service, it removes the means of fixing it.
   */
  management_mode: 'service',
  /** The action's own warning says it: "the connection renegotiates — a brief drop". */
  vlan_mode: 'service',
  /**
   * `vlan_mode`'s own reason and one more: the WAN object is re-stated on the
   * ONU (a PPPoE account re-dials, a DHCP lease is asked for again) and a
   * routed subscriber going bridged has their ONU-terminated WAN stood down.
   * Every row in the run drops for a moment.
   */
  wan_mode: 'service',
  /** A move takes the old service away before the new one exists. */
  vlan_change: 'service',
  /**
   * Naming and re-keying a subscriber's WiFi is `service`, not `write`.
   *
   * Every phone, laptop, TV and camera in the house drops off the moment the
   * key changes and has to be re-joined by hand, by somebody who may not have
   * the new one. That it is undone by another write is true and beside the
   * point — the outage happens on the way there and again on the way back.
   *
   * Not `rebuild`. Nothing is deauthorised and nothing is re-provisioned, and
   * `rebuild` also carries five seconds of pacing per ONU, which exists for
   * PON re-ranging that an SSID write does not cause.
   */
  ssid_set: 'service',
  /**
   * Removal sits on the same rung and is confirmed with a different word.
   *
   * It is worse than a set — the subscriber does not get a network back under
   * a new name, they get nothing — but the ladder measures WHAT KIND of harm,
   * not how much, and this is the same kind: the connection goes away. The
   * step up is `BULK_CONFIRM_PHRASE`, which is per action precisely so that
   * last week's SSID rename does not carry an operator's fingers through this
   * week's removal.
   */
  ssid_remove: 'service',
  /**
   * The same rung as `ssid_set`, for the same reason and one more: it names
   * and re-keys two SSID interfaces per ONU AND attaches a VLAN, and on a
   * secondary interface that already carries somebody's guest network the
   * rename drops every device joined to it.
   */
  hotspot: 'service',
  onu_reboot: 'service',
  onu_recreate: 'rebuild',
  /** `speed_rebind`'s rung, and one more reason: it also rebinds the MANAGEMENT T-CONT the platform reaches the ONU over. */
  profile_migrate: 'service',
};

/**
 * The word an operator types out in full for the loudest runs.
 *
 * Per action rather than one shared word, so muscle memory from last week's
 * reboot does not carry an operator through this week's recreate.
 */
const BULK_CONFIRM_PHRASE: Partial<Record<BulkActionId, string>> = {
  onu_reboot: 'REBOOT',
  onu_recreate: 'RECREATE',
  profile_migrate: 'PROFILES',
  management_mode: 'MANAGEMENT',
  vlan_mode: 'VLAN',
  vlan_change: 'VLAN',
  /**
   * Its own word, not `VLAN` — the word the two VLAN actions share and an
   * operator's fingers already know. This action writes the WAN object, the
   * firewall and the IPv6 half beside the VLAN's treatment, and the word being
   * typed has to be the word for what it does (the `hotspot` rule).
   */
  wan_mode: 'WAN',
  ssid_set: 'SSID',
  /**
   * Deliberately not `SSID`. The two actions differ by one dropdown and the
   * one that takes a network away must not be confirmable with the word the
   * one that adds a network taught the operator to type.
   */
  ssid_remove: 'REMOVE',
  /**
   * Not `SSID` either, and for the rule stated two entries up: this action
   * writes an SSID AND a VLAN, and the word for it has to be the word for
   * what it does.
   */
  hotspot: 'HOTSPOT',
  /**
   * Its own word, and the one the Huawei cost is named with.
   *
   * The dialog that asks for it says what a Huawei rebind does — the
   * service-port is deleted and recreated at the same index, and the subscriber
   * is down for the gap — so the word being typed is the word for that. Not
   * `VLAN`, which the two VLAN actions share and which an operator's fingers
   * already know.
   */
  speed_rebind: 'SPEED',
  router_clock: 'CLOCK',
};

/**
 * The longest explicit tick-list a plan will carry.
 *
 * Named because callers have to respect it BEFORE building a request — the ONU
 * list hands its filtered set over as ids, and a set longer than this has to
 * be refused with a reason rather than truncated, since a silently shortened
 * selection is a bulk run against ONUs nobody chose. Well under the SQLite
 * variable limit; past it a selection is a filter.
 */
export const BULK_ONU_IDS_MAX = 500;

/**
 * Which ONUs.
 *
 * Deliberately the same predicates the ONU list itself offers, so the count an
 * operator sees on screen and the count the plan reports come from the same
 * question. `onuIds` narrows further to an explicit tick-list; supplying both
 * means the INTERSECTION, which is the conservative reading and the one that
 * cannot surprise anybody.
 *
 * `oltId` is REQUIRED, and that is the operator's rule rather than a
 * convenience: a bulk run targets exactly one chassis. It is required HERE, in
 * the schema, rather than only in the form — a caller hitting the API directly
 * must not be able to build a cross-chassis plan, and a plan is the thing an
 * execution is authorised against. It also means every VLAN and PON port
 * picker on the form has a source: there is no "which VLANs, across four
 * chassis that each carry different ones" to answer.
 */
export const bulkOnuFilterSchema = z.object({
  oltId: z.string().min(1).max(64),
  /** `1/3/15` — one PON port on that OLT. */
  ponPort: z.string().max(32).nullable().default(null),
  state: onuStateSchema.nullable().default(null),
  /** An attached VLAN, from whichever side recorded it. */
  vlan: z.number().int().min(1).max(4094).nullable().default(null),
  /**
   * Bridged or routed, from the RECORDED subscriber-VLAN intent (`onu_vlans`
   * rows whose purpose is not management/voip). An ONU whose configuration
   * was never adopted or authorised here records no intent and is matched by
   * NEITHER value — the filter's own hint says so, because on a fleet
   * imported from SmartOLT that is most ONUs until their pages are opened.
   */
  wanMode: z.enum(['bridge', 'route']).nullable().default(null),
  zoneId: z.string().min(1).max(64).nullable().default(null),
  splitterId: z.string().min(1).max(64).nullable().default(null),
  /** The ONU model as the chassis reports it, e.g. `F670L`. */
  onuType: z.string().max(64).nullable().default(null),
  /**
   * The INTENT column. Kept for callers that already send it, and not what a
   * new one should use: it records what this platform last asked for and is
   * never reconciled, so on this fleet it is wrong for 159 of 4,697 ONUs —
   * asking it for `tr069` returns 40 that have never informed anything and
   * misses 119 that are informing right now. Prefer `managementState`.
   */
  managementMode: z.enum(['tr069', 'omci']).nullable().default(null),
  /**
   * What the CHASSIS says, in three states.
   *
   * `tr069_other` is the one that could not be expressed before and is the
   * reason this exists on a bulk filter at all: those ONUs are configured for
   * TR-069 and pointed at an ACS this organisation does not run, so every
   * TR-069 action is inert on them. Being able to SELECT them is what makes
   * them fixable in one run rather than one at a time — and being able to
   * EXCLUDE them is what stops a TR-069 bulk action from silently doing
   * nothing to part of its own target set.
   *
   * An ONU no chassis read has touched matches none of the three, because it
   * has not answered the question yet and guessing is what put the intent
   * column 159 rows out.
   */
  managementState: onuManagementStateSchema.nullable().default(null),
  /** Only ONUs that have informed an ACS, or only those that have not. */
  hasDevice: z.boolean().nullable().default(null),
  /** Serial / chassis name / interface, as the list's search box. */
  search: z.string().max(120).nullable().default(null),
  /**
   * The operator's own naming convention, as a selector.
   *
   * Born from a live order — "resync all ONUs without the HS prefix" — that
   * had to be carried out as a hand-built 171-id tick-list because nothing
   * here could say it. `HS ` is this fleet's hotspot prefix; any label an
   * operator writes works. Matching is case-insensitive, LIKE wildcards in
   * the prefix are taken literally, and an ONU with NO name has no prefix:
   * it never matches `nameStartsWith` and always matches
   * `nameNotStartsWith` — exactly the set that hand-built run froze.
   */
  nameStartsWith: z.string().min(1).max(100).nullable().default(null),
  nameNotStartsWith: z.string().min(1).max(100).nullable().default(null),
  /**
   * An explicit tick-list. Intersected with everything above.
   *
   * Capped well under the SQLite variable limit: a selection longer than this
   * is a filter, and asking for it as ids is how a request becomes unreviewable.
   */
  onuIds: z.array(z.string().min(1).max(64)).max(BULK_ONU_IDS_MAX).nullable().default(null),
});
export type BulkOnuFilter = z.infer<typeof bulkOnuFilterSchema>;

// --- Per-action parameters ----------------------------------------------------

export const bulkVlanModeSchema = z.enum(['bridge', 'route']);

/**
 * How a ROUTED ONU gets its address, in a run of many.
 *
 *   recorded  each row's own addressing — DHCP where the row is bridged or
 *             absent (`defaultAddressing`, the step's own opening value)
 *   dhcp      DHCP on every row
 *   pppoe     the PPPoE ACCOUNT RECORDED ON EACH ROW (username + the
 *             encrypted password, decrypted at execution and never on the
 *             plan); a row with half an account is refused by name
 *   static    the address/mask/gateway RECORDED ON EACH ROW; incomplete =
 *             refused by name
 *
 * The two refusals are a ZTE chassis's: it renders the account or the
 * address into its `wan-ip` line. A Huawei chassis renders no such line —
 * the addressing is a record the CPE takes at its next link — so there the
 * row is passed through as it is, half an account included and said on the
 * row (`addressingReady`'s rule), and a `pppoe`/`static` that DIFFERS from
 * the row is what is refused.
 *
 * There is deliberately no username/password/ip field beside it: a PPPoE
 * account and a static address are per subscriber by definition, and one
 * value across a batch is two ONUs fighting over an address — the same
 * reason `management_mode` refuses a static management address in bulk.
 */
export const bulkWanAddressingSchema = z.enum(['recorded', 'dhcp', 'pppoe', 'static']);
export type BulkWanAddressing = z.infer<typeof bulkWanAddressingSchema>;

/**
 * The Firewall step, per ONU. `current` = where the step OPENS on the dialog
 * (`defaultFirewall`: the CPE's reading over TR-069, else `onus.firewalled`,
 * else assumed on-for-routed / off-for-bridged, said on the row). No "leave
 * as it is" — the page has none (operator, 2026-09-13); every value is
 * WRITTEN, `current` included, the way the dialog re-states it.
 */
export const bulkWanFirewallSchema = z.enum(['current', 'on', 'off']);
export type BulkWanFirewall = z.infer<typeof bulkWanFirewallSchema>;

/**
 * The IP protocol step, per ONU. `current` = the same resolution the step
 * opens on: the row's record → what the CPE reads → the firmware default
 * (dual stack, Auto + DHCPv6-PD, never a refused mode) — `defaultIpv6Intent`.
 * An explicit intent is one pair for the whole run, judged per ONU by
 * `ipv6Refusals`; a refused row lists IPv6 as not applied with the gate's
 * sentence and the rest of the write proceeds. `wanIpv6Schema` is reused, so
 * dual stack with both modes None is refused by the same refine.
 */
export const bulkWanIpv6Schema = z.union([z.literal('current'), wanIpv6Schema]);
export type BulkWanIpv6 = z.infer<typeof bulkWanIpv6Schema>;

const IPV4_RE = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

/**
 * The radios an SSID action addresses. `satisfies` rather than a redeclaration,
 * so this can never drift from `SSID_BAND_PORTS`' own key set.
 */
export const bulkSsidBandSchema = z.enum(['2.4G', '5G']) satisfies z.ZodType<SsidBand>;

/**
 * The WPA flavours the chassis takes.
 *
 * The same three `renderZteWifiCommands` accepts, because they ARE the three
 * `ssid auth wpa <port> ? ` printed on the live C650. Not a superset: a mode
 * this list invented would be a line the chassis rejects, on every ONU in a
 * run, discovered at the first one.
 */
export const bulkSsidWpaModeSchema = z.enum(['wpa-psk', 'wpa2-psk', 'wpa-wpa2-psk']);
export type BulkSsidWpaMode = z.infer<typeof bulkSsidWpaModeSchema>;

export const BULK_SSID_WPA_MODE_LABELS: Record<BulkSsidWpaMode, string> = {
  'wpa-psk': 'WPA-PSK only',
  'wpa2-psk': 'WPA2-PSK only',
  'wpa-wpa2-psk': 'WPA/WPA2-PSK (mixed)',
};

/**
 * What an SSID is called. The charset `renderZteWifiCommands` will actually
 * accept, checked HERE so a name the chassis cannot carry is a 400 on the form
 * rather than a thrown blocker discovered once per ONU in a plan of 2,450.
 *
 * ONE word. ZTE's `ssid ctrl <port> name` takes a single `WORD` (its help:
 * `WORD  SSID name (1-32 character(s))`), proved on gachie1 (2026-09-09):
 * `ssid ctrl wifi_0/2 name Mymanga Public WiFi` answered `^` at the space —
 * after the bulk hotspot's VLAN half had already landed — and the quoted form
 * `name "Mymanga Public WiFi"` answered `%Error 20201: Invalid input detected
 * at '^' marker.Invalid command key word`. `name MymangaPublicWiFi` worked.
 * Both OMCI actions (`ssid_set`, `hotspot`) go through this; the TR-069 preset
 * path names its SSID on the CPE and is not bound by the chassis parser.
 */
export const BULK_SSID_NO_WHITESPACE_REASON =
  "An SSID written over OMCI is ONE word of 1–32 characters — no spaces. ZTE's `ssid ctrl <port> name` takes a single WORD: a name with a space is refused at the space, and the quoted form answers `%Error 20201`. Write MymangaPublicWiFi, not Mymanga Public WiFi.";

export const BULK_SSID_CHARSET_REASON =
  "An SSID name is 1–32 characters — letters, digits and - _ . @ ' only, no spaces.";

const BULK_SSID_NAME_RE = /^[\p{L}\p{N}\-_.@']+$/u;

export const bulkSsidNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .superRefine((name, ctx) => {
    if (/\s/.test(name)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: BULK_SSID_NO_WHITESPACE_REASON });
    } else if (!BULK_SSID_NAME_RE.test(name)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: BULK_SSID_CHARSET_REASON });
    }
  });

/**
 * The same rule, asked per ONU instead of at the boundary: can THE CHASSIS
 * carry this name? Null when it can.
 *
 * `ssid_set` no longer goes down the chassis for every ONU — a TR-069-linked
 * CPE is written on the CPE, where a space is ordinary. 98 of this platform's
 * 262 named devices carry one right now, "Mymanga Public WiFi" among them, so
 * a boundary that refused every spaced name was refusing the fleet's own
 * convention on behalf of a parser most rows never reach. The refusal is not
 * dropped — it is moved to the rows it is true of.
 */
export function chassisSsidNameRefusal(name: string): string | null {
  const trimmed = name.trim();
  if (/\s/.test(trimmed)) return BULK_SSID_NO_WHITESPACE_REASON;
  if (!BULK_SSID_NAME_RE.test(trimmed)) return BULK_SSID_CHARSET_REASON;
  return null;
}

/**
 * What an SSID is called when the CPE is what writes it.
 *
 * Printable, 1–32 characters, and that is the whole of it: an SSID is a
 * subscriber-facing name and the fleet's own are "Mymanga Public WiFi",
 * "MagNet Wifi | Kshs.10", "Nyamz Kinyozi". A control character is refused
 * because it would travel through a TR-069 SOAP body and a chassis transcript
 * as something neither is prepared for; nothing else is this schema's business.
 *
 * `hotspot` keeps `bulkSsidNameSchema` instead, because that action has only
 * the chassis path and a name it cannot carry is a run that lands a VLAN and
 * then fails — which is exactly what happened on 2026-09-09.
 */
export const bulkCpeSsidNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(32)
  .refine((name) => !/[\p{Cc}\p{Cf}]/u.test(name), {
    message: 'An SSID name is 1–32 printable characters.',
  });

/**
 * How an SSID is secured. A UNION, so "open" cannot be reached by leaving the
 * passphrase blank.
 *
 * That distinction is the whole reason this is not two optional fields. The
 * platform's existing single-ONU writer treats an absent key as "leave the
 * authentication alone" — `renderZteWifiCommands` emits no `ssid auth` line at
 * all — and `omci-write.ts` keeps the key out of the rename path on purpose
 * ("setting a subscriber's wifi password is a different act from renaming
 * their network"). An empty box has therefore never meant "no encryption" here
 * and must not start to: on a bulk run it would mean an operator who did not
 * fill in a field left several thousand houses on an open network.
 *
 * So open is a thing you SAY, and `acknowledgeOpen` is the saying of it. It is
 * a required literal `true`, which means a caller skipping the form cannot get
 * there by omission either — the same shape `updateBulkLimitsSchema` uses to
 * make widening the cap an act rather than a default.
 */
export const bulkSsidSecuritySchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('wpa'),
    mode: bulkSsidWpaModeSchema.default('wpa-wpa2-psk'),
    /**
     * 8–63 printable ASCII with no spaces — what the chassis takes, and what
     * consumer devices agree on.
     *
     * It is sent to the chassis and it is stored ENCRYPTED on the plan. It is
     * never returned by the API, never in a rendered command, never in a
     * transcript and never in the audit trail; see `redactBulkParams` and
     * `maskWifiSecrets`.
     */
    passphrase: z
      .string()
      .min(8)
      .max(63)
      .regex(/^[\x21-\x7e]+$/, 'A WiFi passphrase is 8–63 printable characters with no spaces.'),
  }),
  z.object({
    kind: z.literal('open'),
    /**
     * Ticked, not defaulted. See above.
     *
     * A `boolean` that must be `true` rather than a `z.literal(true)`, so the
     * un-ticked state is representable: the form has to be able to hold
     * "open is selected and nobody has agreed to it yet" and keep the Build
     * button off, and a type that can only be `true` would have forced that
     * state to live somewhere the request could not see.
     */
    acknowledgeOpen: z.boolean().refine((v) => v === true, {
      message:
        'An open SSID has no encryption: anyone in range joins it and everything unencrypted on it is readable. Confirm that is what this run is for.',
    }),
  }),
]);
export type BulkSsidSecurity = z.infer<typeof bulkSsidSecuritySchema>;

/**
 * Which of an ONU's sides a firewall run addresses.
 *
 * The operator asked for WAN and management to be separable, and the honest
 * state of that ask is: only one of the three is grammar this fleet has.
 *
 *   all         the whole-ONU OMCI flag, `firewall enable|disable` inside
 *               `pon-onu-mng <iface>`. Probed on a C300 and shipped since
 *               2026-08-27 — this is what `onu_firewall` has always sent, and
 *               it stays the DEFAULT so that an operator depending on it today
 *               is unaffected by this field existing.
 *   wan         only the subscriber-facing side.
 *   management  only the path this platform reaches the ONU on.
 *
 * The last two are REFUSED, by name, with the probe that would lift them:
 * `security-mgmt ?` inside `pon-onu-mng` on the registered test ONU.
 * `security-mgmt` is a real child of that block — the ACS sweep's own
 * running-config reader lists it beside `tr069-mgmt` and `ip-host` — but not
 * one line of it appears in either stored production config, so nothing here
 * knows what parameters it takes or whether it scopes anything at all.
 * Inventing that grammar is the failure mode this codebase has repeatedly paid
 * to avoid, so the control is PRESENT and says what is missing rather than
 * being absent and unexplained.
 */
export const bulkFirewallScopeSchema = z.enum(['all', 'wan', 'management']);
export type BulkFirewallScope = z.infer<typeof bulkFirewallScopeSchema>;

export const BULK_FIREWALL_SCOPE_LABELS: Record<BulkFirewallScope, string> = {
  all: 'The whole ONU',
  wan: 'The WAN side only',
  management: 'The management path only',
};

/**
 * A speed profile named the way the chassis names it.
 *
 * Two alternatives, and both are identifiers a chassis reported:
 *
 *   `SMARTOLT-1G-DOWN`  the ordinary case, and the charset is exactly the one
 *                       `assertBindOnly`'s allowlist in `speed-rebind.ts` will
 *                       let into a rendered line. Checked HERE so a name the
 *                       chassis cannot carry is a 400 on the form rather than a
 *                       thrown blocker discovered once per ONU in a plan of
 *                       2,450 — the same reason `bulkSsidNameSchema` checks its
 *                       own charset at the boundary.
 *   `Traffic table 8`   what `readSpeedProfiles` calls a Huawei traffic table
 *                       whose NAME would not read back. It is still bindable,
 *                       because a Huawei service-port binds by INDEX and
 *                       `tableIndexFor` parses the index straight out of that
 *                       string. It carries a space and so can never reach a ZTE
 *                       bind line; on a ZTE chassis no profile is called that,
 *                       and the plan refuses with `profile-absent-from-chassis`
 *                       rather than rendering something the allowlist would
 *                       throw on.
 *
 * There is no rate here and there must never be one. See `speed_rebind` in
 * `bulkActionIdSchema`.
 */
export const bulkSpeedProfileNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(
    /^(?:[A-Za-z0-9_.:/-]+|Traffic table \d+)$/,
    'A speed profile is named as the chassis names it — letters, digits and - _ . : / only (or "Traffic table 8" for a Huawei table with no readable name).',
  );

/**
 * The action and its settings, as one discriminated union.
 *
 * A union rather than a loose `params` bag so that "this action needs a preset
 * id" is a parse error at the boundary instead of a runtime blocker halfway
 * through a fleet.
 */
export const bulkActionRequestSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal('device_preset'),
    presetId: z.string().min(1).max(64),
  }),
  z.object({
    action: z.literal('management_mode'),
    mode: z.enum(['tr069', 'omci']),
    addressing: z.enum(['dhcp', 'static']).default('dhcp'),
    /**
     * A static management address is per-ONU by definition, so it is not
     * offered here. One address across a batch is two ONUs fighting over it.
     */
    mgmtVlan: z.number().int().min(1).max(4094).nullable().default(null),
    /**
     * Which ACS the batch points its ONUs at, chosen for the RUN.
     *
     * Null keeps the per-ONU resolution — each target's own override, else the
     * OLT default, else the org's local profile — which is what this action
     * always did. A value must name a profile the OLT is associated with and
     * only means anything when `mode` is `tr069`: it is refused alongside
     * `omci`, because a profile silently ignored is an operator who believes
     * they moved a batch's ACS when they did not.
     */
    tr069ProfileId: z.string().max(64).nullable().default(null),
  }),
  z.object({
    action: z.literal('tr069_profile'),
    /** Null clears the per-ONU override, putting the ONU back on the OLT's. */
    profileId: z.string().min(1).max(64).nullable(),
  }),
  z.object({
    action: z.literal('attach_vlan'),
    vlanId: z.number().int().min(1).max(4094),
    mode: bulkVlanModeSchema.default('route'),
    name: z.string().max(64).nullable().default(null),
  }),
  z.object({
    action: z.literal('vlan_mode'),
    vlanId: z.number().int().min(1).max(4094),
    mode: bulkVlanModeSchema,
  }),
  /**
   * The WAN dialog's Apply, for every ONU in the run. Each field is the
   * dialog's step; where the dialog has ONE ONU's current value to open on,
   * this has a word meaning "each ONU's own".
   */
  z.object({
    action: z.literal('wan_mode'),
    /**
     * Null = the ONE subscriber VLAN each ONU records (rows whose purpose is
     * not management/voip — the dialog's `attached[0]`); none or more than one
     * is REFUSED BY NAME, never guessed for (`speed_rebind.vlanId` is the
     * precedent). A number names a VLAN the chassis carries, judged by
     * `judgeVlanPurpose` like `attach_vlan`; it is attached only to an ONU
     * that records NO subscriber VLAN yet (the dialog's own "not on this ONU
     * yet — attached by this change"), and an ONU already carrying another is
     * refused rather than given a second WAN (the `vlan_change` doctrine: a
     * run never turns "not part of it" into "gets a second service").
     */
    vlanId: z.number().int().min(1).max(4094).nullable().default(null),
    /** Routed (the ONU terminates it) or bridged. Required — the run's own question. */
    mode: bulkVlanModeSchema,
    /**
     * Ignored (and must be `recorded`) when `mode` is bridge: a bridged VLAN
     * has no addressing, the dialog drops the step. See the schema's own doc.
     */
    addressing: bulkWanAddressingSchema.default('recorded'),
    /**
     * Static rows only, optional — and the ONE addressing particular that is
     * shared plumbing rather than subscriber state: the row records no DNS
     * (`onu_vlans` has no column; the dialog's DNS box is transient), and
     * `wanIpProfileName` names the chassis IP profile by gateway(+dns)
     * precisely because every subscriber behind one gateway shares it. Null =
     * a gateway-only profile, exactly what a static row re-applied from the
     * dialog with the DNS box empty gets.
     */
    staticDns: z.string().max(64).nullable().default(null),
    /** Absent = `current`, so a stored plan reconstructed without the field does what the page does. */
    ipv6: bulkWanIpv6Schema.default('current'),
    /** Ignored with a note on a Huawei chassis, where the step is absent. */
    firewall: bulkWanFirewallSchema.default('current'),
  }),
  z.object({
    action: z.literal('vlan_change'),
    /** The VLAN the subscriber is on today. An ONU not on it is refused, by name. */
    fromVlanId: z.number().int().min(1).max(4094),
    /** Where they end up. Checked against what the chassis actually carries. */
    toVlanId: z.number().int().min(1).max(4094),
    /**
     * Null keeps whatever treatment the old VLAN had.
     *
     * A move is a move. Silently re-deciding bridged-versus-routed while
     * changing the VLAN id would change two things and report one.
     */
    mode: bulkVlanModeSchema.nullable().default(null),
  }),
  /**
   * Put a name and a key on a chosen WiFi interface.
   *
   * TWO TRANSPORTS, decided per ONU rather than per run: a TR-069-linked CPE
   * is written on the CPE, and an ONU that has never informed is written down
   * the chassis over OMCI. That split is what lets the name be the fleet's own
   * — see `bulkCpeSsidNameSchema` — and the chassis rows carry the one-word
   * refusal by name (`chassisSsidNameRefusal`) rather than the whole run being
   * refused at the boundary for them.
   */
  z.object({
    action: z.literal('ssid_set'),
    ssid: bulkCpeSsidNameSchema,
    /**
     * Which radios. Both is the ordinary answer and one is a deliberate
     * narrowing, so it is a LIST rather than a `'both' | '2.4G' | '5G'`: an
     * ONU that can serve one of the two named bands and not the other is the
     * common case on this fleet, and the plan has to be able to write the half
     * it can while saying, on that ONU's row, why the other half did not
     * happen. A single enum would have forced that into a yes/no.
     */
    bands: z.array(bulkSsidBandSchema).min(1).max(2),
    /**
     * Which interface, per band. Null is that band's primary — `wifi_0/1` and
     * `wifi_0/5` — and the primary is the subscriber's OWN network.
     *
     * So an operator adding a hotspot alongside what is already there names a
     * secondary interface here, and one re-keying the house network leaves
     * these null. Both are real requests and the plan tells each ONU's row
     * apart: it reads the last chassis snapshot and says whether the interface
     * being written is empty today or already carries a name.
     *
     * Whatever is chosen is checked against what the ONU's MODEL exposes.
     * `wifi_0/6` is refused on an HG8546M by name, never redirected.
     *
     * `NEXT_FREE_SSID_PORT` is the third answer and the one a hotspot wants:
     * not a number at all, but "whichever slot on this band is free", resolved
     * per ONU because the answer differs per vendor. See its own doc.
     */
    port24: z.string().max(16).nullable().default(null),
    port5g: z.string().max(16).nullable().default(null),
    /**
     * Bind the interfaces this run writes to a VLAN, bridged, access.
     *
     * Null is today's behaviour and the default: the SSID is named and keyed
     * and nothing else is touched. A number is the second half of what an
     * operator means by "add the public network" — a name on a radio that is
     * bound to nothing carries nothing, which is the gap the operator hit
     * (2026-09-24: "vlan should be an option when add a vlan in bulk").
     *
     * It is a CHASSIS write even when the SSID half goes over TR-069, because
     * the binding is `vlan port <iface> mode hybrid def-vlan <vlan>` on the
     * OLT. ZTE only: `vlan port ?` accepts `wifi_slot/port` there and a Huawei
     * OLT has no equivalent for a wifi interface at all, so it is refused by
     * name rather than half-written.
     */
    vlanId: z.number().int().min(1).max(4094).nullable().default(null),
    security: bulkSsidSecuritySchema,
  }),
  z.object({
    action: z.literal('ssid_remove'),
    /**
     * WHICH INTERFACE, per band — one each, the same picker and the same
     * vocabulary as the add half (operator, 2026-09-24: "you select the ssids
     * you want to clear/delete … you select one when adding", and "2.4ghz is
     * wifi 1,2,3,4 and 5ghz is wifi5,6,7,8").
     *
     * This reverses an earlier rule and the reason that rule existed still
     * stands, so it is answered rather than deleted: a slot holds whatever
     * that subscriber happens to have in it, which on a fleet where installers
     * have been setting up guest networks for years is somebody's own network
     * on some rows. So the SLOT is what the operator chooses and the NAME is
     * what the plan reports — every row reads back the name the CPE has on
     * that interface today, and a plan is read before it runs.
     *
     * Null is NOT "the primary" here, which is the one place this deliberately
     * differs from `ssid_set`. An unfilled box there re-keys the house
     * network, an ordinary request; an unfilled box here would TURN THE HOUSE
     * NETWORK OFF across a fleet. Every addressed band needs its interface
     * named (`createBulkPlanSchema`'s `superRefine`).
     */
    port24: z.string().max(16).nullable().default(null),
    port5g: z.string().max(16).nullable().default(null),
    /**
     * The OLDER form: a removal matched by name, with no interface picker.
     *
     * Kept parseable because the ledger holds runs made with it — a plan's
     * params are re-read by `GET /plans/:id` and by every execution — and a
     * record that stops parsing is a record that stops being readable. New
     * requests carry interfaces; this is null on them.
     */
    ssid: z.string().trim().min(1).max(32).nullable().default(null),
    bands: z.array(bulkSsidBandSchema).min(1).max(2),
    /**
     * DELETE the network rather than switch it off, where the CPE can lose one.
     *
     * False — the default, and every plan in the ledger before 2026-09-25 — is
     * the switch-off this action has always been: the WLAN, its name and its
     * key stay on the CPE. True takes the network away for good: a Huawei loses
     * the WLAN object (DeleteObject, live-verified on the HG8546M, HG8145V,
     * HG8145V6 and HG8145X6-10 by the 2026-09-25 cleanup); a CPE that keeps a
     * fixed set of eight (ZTE F670L, Nokia) has the slot switched off and put
     * back to its factory name. Either way the platform forgets it — stored
     * name, stored key, queued writes — and a ZTE chassis loses the socket's
     * VLAN binding. Never the band's own network: WiFi 1 and WiFi 5 are
     * refused at this boundary and again per ONU.
     */
    delete: z.boolean().default(false),
  }),
  /**
   * A public hotspot: a bridged VLAN and a public SSID on both radios.
   *
   * The interfaces DEFAULT rather than being nullable-for-primary as they are
   * on `ssid_set`, because a hotspot is by definition the network ALONGSIDE
   * the subscriber's own: `wifi_0/2` and `wifi_0/6` are the operator's second
   * slot on each radio. Both are still checked against the band layout at the
   * boundary and against the ONU's model in the plan, and a model that lacks
   * one of them gets the other with a note; one that lacks both is refused.
   *
   * `security` is the SAME union `ssid_set` takes, so the passphrase is
   * encrypted on the plan by the same code and "open" is a thing that has to
   * be SAID — and is then refused by name in the plan, because no unencrypted
   * auth form has been captured on any ZTE chassis. It is nullable only so a
   * DISABLE run has nothing to type: enabling with no security is refused in
   * `createBulkPlanSchema`'s `superRefine`, so an enable never lands on an
   * ONU without a key.
   */
  z.object({
    action: z.literal('hotspot'),
    enabled: z.boolean(),
    vlanId: z.number().int().min(1).max(4094),
    // One WORD: the chassis parser stops at a space (see `bulkSsidNameSchema`).
    ssid: bulkSsidNameSchema.default('MymangaPublicWiFi'),
    security: bulkSsidSecuritySchema.nullable().default(null),
    port24: z.string().max(16).default('wifi_0/2'),
    port5g: z.string().max(16).default('wifi_0/6'),
  }),
  z.object({
    action: z.literal('zone'),
    zoneId: z.string().min(1).max(64).nullable(),
  }),
  z.object({
    action: z.literal('firmware_campaign'),
    name: z.string().trim().min(1).max(120),
    fileName: z.string().min(1).max(200),
    targetVersion: z.string().min(1).max(64),
    /** 1 by default: the canary the campaign engine already defaults to. */
    batchSize: z.number().int().min(1).max(200).default(1),
    pauseOnFailure: z.boolean().default(true),
  }),
  /**
   * The four whole-ONU actions the ONU detail page offers, in bulk.
   *
   * None of them carries a setting: each one does the same thing to every ONU
   * it names, which is exactly why they are safe to express as a bare literal
   * and exactly why the confirmation, not a parameter, is what distinguishes
   * them. `BULK_ACTION_RISK` is where they differ.
   */
  z.object({
    action: z.literal('onu_resync'),
    /**
     * Reboot each ONU first and write while it is off the PON (2026-10-16):
     * how a word an ONT declines while up gets stored on a ZTE chassis. It
     * is confirmed exactly like a reboot (the service rung: the chassis's
     * name, and REBOOT in full when it covers the whole chassis).
     */
    reboot: z.boolean().optional(),
  }),
  z.object({ action: z.literal('onu_adopt') }),
  z.object({ action: z.literal('onu_summon') }),
  /**
   * One intent for the whole run, judged per ONU by `ipv6Refusals` — a row
   * whose CPE has no observed word for a mode is reported with that sentence
   * and nothing is written to it. No `current`: this action exists to SET a
   * protocol, and "as each ONU has it" is what not running it means.
   */
  z.object({ action: z.literal('wan_ipv6'), ipv6: wanIpv6Schema }),
  /** No setting: the zone is the platform's and the servers are the org's (Settings → NTP). */
  z.object({ action: z.literal('router_clock') }),
  z.object({
    action: z.literal('onu_firewall'),
    enabled: z.boolean(),
    /**
     * Defaulted rather than required, which is the whole no-regression rule in
     * one line: a caller that has been sending `{ action, enabled }` since this
     * action shipped keeps getting exactly the write it has always got.
     */
    scope: bulkFirewallScopeSchema.default('all'),
  }),
  /**
   * Bind these subscribers to profiles the chassis already holds.
   *
   * Both directions are optional and at least one is required (enforced in
   * `createBulkPlanSchema`'s `superRefine`, because a discriminated-union
   * member cannot check across its own fields). Naming only one leaves the
   * other exactly as it is — which on a C650 means the plan has to carry the
   * untouched direction forward onto the service-port line it re-states, and it
   * refuses the ONU by name when it cannot read what that direction currently
   * is.
   */
  z.object({
    action: z.literal('speed_rebind'),
    /** The upstream profile, by name. Null leaves the upstream alone. */
    upProfile: bulkSpeedProfileNameSchema.nullable().default(null),
    /** The downstream profile, by name. Null leaves the downstream alone. */
    downProfile: bulkSpeedProfileNameSchema.nullable().default(null),
    /**
     * WHICH service the rate belongs to, where a chassis binds rates per
     * service rather than per ONU.
     *
     * A C650 binds them on a vport service-port and Huawei on a config-level
     * one, and an ONU carries several: the subscriber's internet, and the
     * management VLAN whose service-port is on traffic table 7 on both Huawei
     * chassis, 1,949 times. Rebinding the wrong one changes the speed of the
     * path this platform manages the ONU over.
     *
     * Null means "the one subscriber VLAN SimpleACS records for this ONU", and
     * an ONU that records none — or more than one — is REFUSED BY NAME rather
     * than having one picked for it. A C300 ignores this: it binds both
     * directions on the ONU interface itself, per T-CONT and GEM, not per VLAN.
     */
    vlanId: z.number().int().min(1).max(4094).nullable().default(null),
  }),
  z.object({ action: z.literal('onu_reboot') }),
  z.object({ action: z.literal('onu_recreate') }),
  z.object({
    action: z.literal('profile_migrate'),
    /**
     * Which profile NAME becomes which, per kind: `tcont` (upstream, `tcont N
     * profile X`) or `traffic` (downstream, `gemport N traffic-limit
     * downstream X`). Per kind because one name can be both — SmartOLT's
     * `SMARTOLT-VOIPMNG-10M` is a T-CONT profile AND a traffic profile, and
     * the two want different SimpleACS names (`M1000-UP` / `M1000-DOWN`).
     * A binding on a name no pair names is left exactly as it is.
     */
    pairs: z
      .array(
        z.object({
          kind: z.enum(['tcont', 'traffic']),
          from: bulkSpeedProfileNameSchema,
          to: bulkSpeedProfileNameSchema,
        }),
      )
      .min(1)
      .max(16),
  }),
]);
export type BulkActionRequest = z.infer<typeof bulkActionRequestSchema>;
export type ProfileMigrationPair = Extract<BulkActionRequest, { action: 'profile_migrate' }>['pairs'][number];

export const createBulkPlanSchema = z
  .object({
    filter: bulkOnuFilterSchema,
    request: bulkActionRequestSchema,
    /**
     * Send the settings even where this platform believes they are already there.
     *
     * Four preconditions read from SimpleACS's OWN RECORD rather than from the
     * chassis: already on that TR-069 profile, already inheriting the OLT's,
     * already carrying that VLAN, already in that VLAN mode. Each refuses the
     * ONU, and each is only as true as a record that can drift — which on this
     * fleet it demonstrably does. An operator who can see the chassis disagrees
     * had no way to say so.
     *
     * With this set those four become WARNINGS on the row instead of blockers,
     * so the row still says what it noticed and the write goes anyway. It
     * changes nothing else: a refusal for a reason the CHASSIS gave, an ONU
     * this platform holds no VLANs for, a band the model has not got, a vendor
     * that does not support the action — all still refuse, because those are
     * facts about the hardware rather than beliefs about it.
     *
     * Plan-time only. Execution runs the rows the plan froze, so a plan built
     * without it cannot acquire it later.
     */
    force: z.boolean().optional(),
  })
  /**
   * Cross-field rules that a discriminated union member cannot carry itself.
   *
   * They live at the boundary rather than in the service so that a caller
   * hitting the API directly is refused by the same rule the form obeys — a
   * plan is the object an execution is authorised against, so anything the
   * form would not let somebody build must not be buildable at all.
   */
  .superRefine((value, ctx) => {
    if (value.request.action === 'profile_migrate') {
      const seen = new Set<string>();
      value.request.pairs.forEach((pair, index) => {
        if (pair.from === pair.to) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['request', 'pairs', index, 'to'],
            message: `${pair.from} is mapped to itself — a pair moves a binding from one profile to ANOTHER.`,
          });
        }
        const key = `${pair.kind}:${pair.from}`;
        if (seen.has(key)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['request', 'pairs', index, 'from'],
            message: `${pair.from} is named twice for ${pair.kind === 'tcont' ? 'T-CONTs' : 'traffic'} — one name can move to one profile.`,
          });
        }
        seen.add(key);
      });
    }
    if (value.request.action === 'vlan_change' && value.request.fromVlanId === value.request.toVlanId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['request', 'toVlanId'],
        message:
          'The VLAN to move from and the VLAN to move to are the same. To change bridged/routed without changing the VLAN, use "WAN mode".',
      });
    }

    if (value.request.action === 'wan_mode') {
      const r = value.request;
      /**
       * A bridged VLAN has no ONU-side addressing — the dialog drops the
       * Addressing and IP protocol steps when Bridging is chosen, so a bridged
       * request that names an addressing, an IPv6 intent or a DNS is a request
       * the form could not have built. Refused at the boundary rather than
       * silently ignored: a run recorded as "bridge, PPPoE as recorded" would
       * read in the ledger as though the account had been sent.
       */
      if (r.mode === 'bridge' && (r.addressing !== 'recorded' || r.ipv6 !== 'current' || r.staticDns !== null)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['request', r.addressing !== 'recorded' ? 'addressing' : r.ipv6 !== 'current' ? 'ipv6' : 'staticDns'],
          message:
            "A bridged VLAN has no ONU-side addressing — the subscriber's own router does DHCP/PPPoE and IPv6. Choose Routing, or leave these as recorded/current.",
        });
      }
      /** A DNS rides the static IP profile and nothing else; beside DHCP or PPPoE it is a value nothing would send. */
      if (r.staticDns !== null && (r.addressing === 'dhcp' || r.addressing === 'pppoe')) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['request', 'staticDns'],
          message:
            'A primary DNS is part of a static WAN’s chassis IP profile — it means nothing beside DHCP or PPPoE. Choose Static or "as recorded", or clear it.',
        });
      }
      if (r.staticDns !== null && !IPV4_RE.test(r.staticDns.trim())) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['request', 'staticDns'],
          message: `"${r.staticDns}" is not a valid primary DNS address (an IPv4 address such as 8.8.8.8).`,
        });
      }
    }

    /**
     * A rebind that names neither direction is a run that writes nothing to
     * every ONU it matched.
     *
     * `planSpeedRebind` would refuse each target with `no-change`, which is a
     * correct answer repeated once per subscriber; a plan of 2,450 identical
     * refusals is how an operator learns to scroll past refusals. It is one
     * mistake about the REQUEST, so it is refused where the request is parsed.
     */
    if (
      value.request.action === 'speed_rebind' &&
      value.request.upProfile === null &&
      value.request.downProfile === null
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['request', 'downProfile'],
        message:
          'Name the upstream profile, the downstream profile, or both. A rebind that names neither would leave every ONU it matched exactly as it is.',
      });
    }

    if (value.request.action === 'ssid_set') {
      /**
       * A chosen interface has to be ON the band it was chosen for.
       *
       * Refused at the boundary rather than in the resolver, because this is
       * the one mistake that would not look like a mistake: `wifi_0/2` in the
       * 5 GHz box builds a plan whose every row reads "5 GHz → wifi_0/2" and
       * whose every write renames and re-keys the subscriber's 2.4 GHz
       * network. `ssidPortForBand` cannot catch it — it is answering "does
       * this ONU expose that interface", and the answer is yes.
       */
      const chosen: Array<[SsidBand, string | null | undefined, 'port24' | 'port5g']> = [
        ['2.4G', value.request.port24, 'port24'],
        ['5G', value.request.port5g, 'port5g'],
      ];
      for (const [band, port, field] of chosen) {
        // "Whichever is free" names no interface, so there is no band to check.
        if (!port || isNextFreeSsidPort(port)) continue;
        const actual = bandForSsidPort(port);
        if (actual === band) continue;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['request', field],
          message:
            actual === null
              ? `"${port}" is not an SSID interface — they are wifi_0/1 through wifi_0/8.`
              : `${port} is a ${actual} interface in this fleet's layout, so it cannot carry the ${band} name. Writing it there would rename the subscriber's ${actual} network.`,
        });
      }
      /** A band with no interface chosen is fine; a chosen interface on an unrequested band is not. */
      for (const [band, port, field] of chosen) {
        if (port && !value.request.bands.includes(band)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['request', field],
            message: `${port} was chosen for ${band}, but ${band} is not one of the bands this run addresses.`,
          });
        }
      }
    }

    if (value.request.action === 'ssid_remove') {
      const r = value.request;
      const chosen: Array<[SsidBand, string | null | undefined, 'port24' | 'port5g']> = [
        ['2.4G', r.port24, 'port24'],
        ['5G', r.port5g, 'port5g'],
      ];
      for (const [band, port, field] of chosen) {
        if (!port) continue;
        const actual = bandForSsidPort(port);
        if (actual !== band) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['request', field],
            message:
              actual === null
                ? `"${port}" is not an SSID interface — they are wifi_0/1 through wifi_0/8.`
                : `${port} is a ${actual} interface in this fleet's layout, so choosing it for ${band} would turn off the subscriber's ${actual} network instead.`,
          });
        }
        if (!r.bands.includes(band)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['request', field],
            message: `${port} was chosen for ${band}, but ${band} is not one of the bands this run addresses.`,
          });
        }
      }
      /**
       * Every addressed band needs an interface — unless this is one of the
       * older by-name requests, which carried no picker at all.
       *
       * Defaulting an empty box to the primary is what `ssid_set` does and is
       * exactly wrong here: `wifi_0/1` is the subscriber's own network, and a
       * fleet run that quietly took it off would be the single most damaging
       * thing this page could do.
       */
      /**
       * A delete never takes a band's own network. Switching WiFi 1 off is a
       * thing an operator may choose to do to a fleet; deleting it is not
       * offered at all (operator, 2026-09-25: "except on SSID1/SSID5").
       */
      if (r.delete) {
        for (const [band, port, field] of chosen) {
          if (!port || ssidIndexOf(port) !== SSID_BAND_PORTS[band][0]) continue;
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['request', field],
            message: `${port} is the subscriber's own ${band} network, and a delete run never takes it. Choose another WiFi, or untick Delete to switch it off instead.`,
          });
        }
      }
      if (!r.ssid) {
        for (const [band, port, field] of chosen) {
          if (!r.bands.includes(band) || port) continue;
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['request', field],
            message: `Choose which ${band} WiFi to turn off (${band === '2.4G' ? 'WiFi 1–4' : 'WiFi 5–8'}). Nothing is assumed here — an empty box would mean the subscriber's own network.`,
          });
        }
      }
    }

    if (value.request.action === 'hotspot') {
      /**
       * The same cross-band rule as `ssid_set`, on the two interfaces a
       * hotspot always addresses. `wifi_0/6` in the 2.4 GHz box would put the
       * public name on the subscriber's 5 GHz slot and the plan would read as
       * though it had not.
       */
      const chosen: Array<[SsidBand, string, 'port24' | 'port5g']> = [
        ['2.4G', value.request.port24, 'port24'],
        ['5G', value.request.port5g, 'port5g'],
      ];
      for (const [band, port, field] of chosen) {
        const actual = bandForSsidPort(port);
        if (actual === band) continue;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['request', field],
          message:
            actual === null
              ? `"${port}" is not an SSID interface — they are wifi_0/1 through wifi_0/8.`
              : `${port} is a ${actual} interface in this fleet's layout, so it cannot carry the ${band} hotspot SSID.`,
        });
      }
      /**
       * An enable with no security is refused HERE, not defaulted to open and
       * not defaulted to "leave the key alone": a public SSID written with
       * whatever key the interface happened to hold — or none — is exactly the
       * outcome a required field exists to prevent. A disable carries none
       * and needs none; it hides, it does not re-key.
       */
      if (value.request.enabled && value.request.security === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['request', 'security'],
          message:
            'Enabling a hotspot needs a WPA/WPA2 passphrase for the public SSID (8–63 printable characters, no spaces). A disable needs none.',
        });
      }
    }
  });
export type CreateBulkPlanInput = z.infer<typeof createBulkPlanSchema>;

/**
 * Does this filter narrow anything at all beyond naming the chassis?
 *
 * "Every ONU on this OLT" is a different request from "the 12 on port 1/3/15",
 * and now that the shipped limit is ALL it is the difference between a normal
 * run and one that touches every subscriber a chassis carries. It is derived
 * from the filter rather than from a count, deliberately: a count needs a
 * database and moves between the review and the run, whereas the filter is
 * frozen on the plan and the browser and the server can read the same answer
 * out of it without asking each other.
 */
export function isWholeChassisFilter(filter: BulkOnuFilter): boolean {
  return (
    filter.ponPort === null &&
    filter.state === null &&
    filter.vlan === null &&
    /**
     * wanMode and managementState were missing here from the day each was
     * added, so a selection narrowed only by one of them confirmed as the
     * whole chassis. Over-asking is the safe direction to be wrong in, but it
     * was still wrong: the ladder's own doctrine is that it measures how much
     * of the fleet a run covers, and "the 21 ONUs on somebody else's ACS" is
     * not the chassis. Found live when run #20's execute demanded MANAGEMENT
     * for exactly that selection.
     */
    filter.wanMode === null &&
    filter.managementState === null &&
    filter.zoneId === null &&
    filter.splitterId === null &&
    filter.onuType === null &&
    filter.nameStartsWith === null &&
    filter.nameNotStartsWith === null &&
    filter.managementMode === null &&
    filter.hasDevice === null &&
    (filter.search === null || filter.search.trim() === '') &&
    filter.onuIds === null
  );
}

/**
 * What an operator has to type before this run happens.
 *
 * One rung per step up the risk ladder, and a whole-chassis selection moves
 * every rung except a read up by one more. That is the whole answer to "a cap
 * that stops an accident is not the same thing as a cap that stops the
 * operator doing their job": the numeric cap defaults to ALL, and what
 * replaces it is proportion — reading every ONU on a chassis asks for the
 * count, rebooting every ONU on a chassis asks for the count, the chassis's
 * own name, and the word REBOOT.
 *
 * Computed from the ACTION and the FILTER, both of which are frozen on the
 * plan, so the browser renders exactly the fields the server will insist on.
 * The server does not trust that: it recomputes this from the stored plan and
 * compares, which is what stops a direct caller executing a fleet-wide recreate
 * with nothing but a number.
 */
export interface BulkConfirmation {
  risk: BulkActionRisk;
  /** True when the filter names a chassis and narrows nothing further. */
  wholeChassis: boolean;
  /** The chassis's own name, confirmed back. Trimmed, case-insensitive. */
  oltName: boolean;
  /** A word matched in full, exactly as given. Null when none is asked for. */
  phrase: string | null;
}

export function bulkConfirmation(
  action: BulkActionId,
  filter: BulkOnuFilter,
  request?: { reboot?: boolean } | null,
): BulkConfirmation {
  // A resync in the reboot window drops every subscriber it touches for a
  // minute or two: the reboot's own rung and word, not a write's.
  const rebootWindow = action === 'onu_resync' && request?.reboot === true;
  const risk = rebootWindow ? 'service' : BULK_ACTION_RISK[action];
  const wholeChassis = isWholeChassisFilter(filter);
  const phrase = rebootWindow ? 'REBOOT' : (BULK_CONFIRM_PHRASE[action] ?? 'CONFIRM');

  switch (risk) {
    /**
     * A read of the whole chassis is still a read.
     *
     * It is the one rung that does not escalate, and that is on purpose: making
     * an operator type a word to re-read their own fleet teaches them to type
     * words, which is the habit that gets a reboot confirmed without looking.
     */
    case 'read':
      return { risk, wholeChassis, oltName: false, phrase: null };
    case 'write':
      return { risk, wholeChassis, oltName: wholeChassis, phrase: null };
    case 'service':
      return { risk, wholeChassis, oltName: true, phrase: wholeChassis ? phrase : null };
    case 'rebuild':
      return { risk, wholeChassis, oltName: true, phrase };
  }
}

/**
 * Executing names a plan and repeats its number back.
 *
 * `confirmCount` is not decoration: the server compares it to the plan's own
 * eligible count and refuses on any difference. An operator who types the
 * number has read the number.
 *
 * The other two are the rungs above it, and they are OPTIONAL in the schema
 * and MANDATORY in `bulkConfirmation` on purpose — which of them a given plan
 * needs depends on that plan's own action and filter, so the shape cannot
 * decide it and the server has to. Sending one that was not asked for is
 * harmless; omitting one that was is refused.
 */
export const executeBulkPlanSchema = z.object({
  confirmCount: z.number().int().min(0).max(100_000),
  /** The chassis's own name. Required from the `service` rung upwards. */
  confirmOltName: z.string().max(200).nullable().default(null),
  /** The action's word, in full. Required for a rebuild, or a whole-chassis service run. */
  confirmPhrase: z.string().max(64).nullable().default(null),
});
export type ExecuteBulkPlanInput = z.infer<typeof executeBulkPlanSchema>;

/**
 * How long to wait between two targets of the same run.
 *
 * The per-OLT lock already means one CLI session at a time, so this is not
 * about the chassis's CPU — it is about what happens on the PON. Reboots and
 * recreates each end with an ONU dropping off and re-ranging, and a thousand
 * of those back to back is a thousand ranging attempts on one fibre with the
 * chassis's DBA re-converging through all of them. Paced, the same thousand is
 * a rolling window of one or two units re-ranging at a time, which is what the
 * network sees on an ordinary evening anyway.
 *
 * Reads and ordinary writes get nothing added: they leave the ONU up, and the
 * lock's own serialisation is already the whole of their cost.
 */
export const BULK_PACING_MS: Record<BulkActionRisk, number> = {
  read: 0,
  write: 0,
  service: 2_000,
  rebuild: 5_000,
};

// --- What comes back ----------------------------------------------------------

export const bulkPlanTargetSchema = z.object({
  onuId: z.string(),
  onuNumber: z.number().int().nullable(),
  serial: z.string(),
  name: z.string().nullable(),
  interfaceName: z.string().nullable(),
  oltId: z.string(),
  oltName: z.string().nullable(),
  vendor: z.string().nullable(),
  /** False when `blockers` is non-empty. Nothing is written to it either way. */
  applicable: z.boolean(),
  blockers: z.array(z.string()),
  warnings: z.array(z.string()),
  /** One line per thing this would change, in an operator's words. */
  summary: z.array(z.string()),
  /** The exact chassis commands, secrets already masked. Empty for a device-side write. */
  commands: z.array(z.string()),
  /**
   * What happened to this ONU. Null means the plan has not run.
   *
   *   ok         written, and the chassis took it.
   *   failed     attempted and refused — by the chassis, or by a guard that
   *              stood between the plan and a live subscriber.
   *   skipped    deliberately not attempted: the ONU moved since the review,
   *              or it no longer exists.
   *   abandoned  the run STOPPED BEFORE REACHING IT. Nothing was written and
   *              nothing was decided about it. This is the one that used to be
   *              indistinguishable from "the plan never ran": a run that died
   *              at target 41 of 72 left 31 rows at null on a plan marked
   *              executed, which reads exactly like an untouched draft. An
   *              abandoned target is the resumable half of a half-done run.
   *   refused    in the enum since the beginning and never written: a target
   *              refused at PLAN time is counted out of `eligibleCount` and
   *              keeps a null outcome, because it was never part of the run.
   *
   * Which is why `abandoned` had to be its own word rather than a `skipped`
   * with a different sentence on it: "we decided not to" and "we never got
   * there" are answers to different questions, and only one of them is work
   * still owed to a subscriber.
   */
  outcome: z.enum(['ok', 'failed', 'refused', 'skipped', 'abandoned']).nullable(),
  message: z.string().nullable(),
  transcript: z.string().nullable(),
  finishedAt: z.number().int().nullable(),
});
export type BulkPlanTarget = z.infer<typeof bulkPlanTargetSchema>;

/**
 * `running` is not cosmetic — `effectiveOutcome` depends on it.
 *
 * Execution claims the plan before its first write, and the claim used to set
 * `executed`. That made a run IN PROGRESS indistinguishable from one that had
 * stopped: every target the loop had not reached yet was an applicable row with
 * no outcome on an `executed` plan, which is the exact definition of
 * `abandoned`. A plan two minutes into a ten-minute run reported most of its
 * fleet as never-reached, and the resume button offered to finish work that was
 * being done as it was read.
 *
 * With `running` the same rule gives the right answer without being changed:
 * pending targets fall through `planStatus !== 'executed'` and count as
 * nothing, and only when the run finishes and the status becomes `executed`
 * does an outcome-less applicable row mean what it has always meant.
 *
 * A plan left `running` by a killed process is repaired at startup rather than
 * being trusted — see `recoverInterruptedBulkRuns`.
 */
export const bulkPlanStatusSchema = z.enum([
  'draft',
  'running',
  'executed',
  'expired',
  'failed',
]);
export type BulkPlanStatus = z.infer<typeof bulkPlanStatusSchema>;

/**
 * One reason, and how many ONUs it was true of.
 *
 * The plan already separates a refusal from a failure — a refused target is
 * counted out of `eligibleCount`, is never executed, and never gets an
 * `outcome` — so the taxonomy was right before this existed. What was missing
 * was the SIZE of a reason, and it went missing at exactly the scale where it
 * matters: "add a 5 GHz SSID to this chassis" against 2,165 Huawei HG8546Ms is
 * a correct plan in which two thousand rows say the same true sentence, and an
 * operator scrolling those rows reads a broken run. Grouped, it is one line —
 * "2,165 · is a HG8546M, which has no 5G radio" — beside the 287 that will be
 * written. Neither lumped in with errors nor hidden.
 *
 * Grouped on the reason's own text rather than on a category tag, deliberately.
 * Every refusal in this platform is written as a sentence about one ONU with
 * the fact in it, so identical facts produce identical sentences and there is
 * nothing to keep in sync — and no reason gets privileged over another. "No
 * CLI credentials stored" counts itself the same way.
 *
 * With one correction, which is the whole reason `serial` is an argument: those
 * sentences NAME the ONU, usually first. Grouped raw, two thousand ONUs with
 * one fact between them produce two thousand groups of one and the tally is
 * worth nothing. So each target's own serial is taken out of its own reason
 * before the key is made. Only its own — a sentence naming a DIFFERENT unit is
 * saying something about that other unit and has not been generalised.
 *
 * What is left is a clause rather than a sentence ("is a HG8546M, which has no
 * 5G radio …"), which is what a tally row wants: the count is the subject.
 */
export const bulkRefusalGroupSchema = z.object({
  reason: z.string(),
  /**
   * How many ONUs gave this reason — a tally of REASONS, not a partition of
   * ONUs. One ONU can be refused for two things at once (both bands of an SSID
   * run, on a model that can serve neither) and is counted in both, so these
   * can sum to more than the number of ONUs that will not be written. That
   * number is `targetCount - eligibleCount` and is reported separately.
   */
  count: z.number().int(),
  /** The first few, so a group can be recognised without opening the table. */
  serials: z.array(z.string()),
});
export type BulkRefusalGroup = z.infer<typeof bulkRefusalGroupSchema>;

/**
 * A plan's stored settings, as a client may see them.
 *
 * `bulk_plans.params` is the action's request with the action name taken out,
 * and the rule on that column has always been "ids, never credentials". One
 * action now breaks that rule as far as the DATABASE is concerned — an SSID
 * write needs the passphrase again at execution, hours after the plan was
 * reviewed, and there is nowhere else on a single-use plan to keep it — so it
 * is stored ENCRYPTED, under `passphraseEnc`, and the rule is restored here:
 * nothing whose name ends in `Enc` leaves the server, and what leaves in its
 * place is a boolean saying that a key is set.
 *
 * The same shape `toDevicePresetDto` takes with `hasWifiPassphrase`, and for
 * the same reason: a mapper that never reads the encrypted field is a mapper
 * no read path can leak through. This one goes further and refuses to pass
 * anything password-shaped whatever it is called, so a future action that
 * forgets to encrypt is a missing value rather than a published secret.
 */
export function redactBulkParams(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (key.endsWith('Enc')) {
      const base = key.slice(0, -3);
      out[`has${base.charAt(0).toUpperCase()}${base.slice(1)}`] = Boolean(value);
      continue;
    }
    if (/passphrase|password|secret|token|\bkey\b/i.test(key)) {
      out[key] = value === null || value === undefined || value === '' ? null : '********';
      continue;
    }
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      out[key] = redactBulkParams(value as Record<string, unknown>);
      continue;
    }
    out[key] = value;
  }
  return out;
}

/** How many serials a group carries as examples. Enough to recognise, not a list. */
const REFUSAL_EXAMPLES = 5;

/** One ONU's refusal with its own name taken out, so two of them can be one row. */
export function generaliseBulkRefusal(reason: string, serial: string): string {
  if (!serial) return reason.trim();
  const escaped = serial.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return reason
    .replace(new RegExp(`${escaped}(?:'s|’s)?`, 'g'), '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export function groupBulkRefusals(
  targets: ReadonlyArray<{ applicable: boolean; blockers: string[]; serial: string }>,
): BulkRefusalGroup[] {
  const groups = new Map<string, BulkRefusalGroup>();
  for (const target of targets) {
    if (target.applicable) continue;
    for (const blocker of target.blockers) {
      const reason = generaliseBulkRefusal(blocker, target.serial);
      const group = groups.get(reason) ?? { reason, count: 0, serials: [] };
      group.count += 1;
      if (group.serials.length < REFUSAL_EXAMPLES) group.serials.push(target.serial);
      groups.set(reason, group);
    }
  }
  return [...groups.values()].sort((a, b) => b.count - a.count || a.reason.localeCompare(b.reason));
}

export const bulkPlanSchema = z.object({
  id: z.string(),
  /** The run's public name — "run #17". Null only on rows from before numbering. */
  number: z.number().int().nullable().optional(),
  orgId: z.string().nullable(),
  /**
   * The one chassis this plan is about, named on the row itself and not only
   * inside the filter blob.
   *
   * "Which OLT did that run on" is the first question anyone asks reading back
   * a bulk run at 2am, and the answer has to survive the OLT being renamed or
   * deleted — so the name is denormalised beside the id.
   */
  oltId: z.string(),
  oltName: z.string().nullable(),
  action: bulkActionIdSchema,
  /** What the action was asked to do, with any secret already removed. */
  params: z.record(z.unknown()),
  filter: bulkOnuFilterSchema,
  status: bulkPlanStatusSchema,
  /** Every ONU the filter matched — the "191 ONU(s)" the operator sees. */
  targetCount: z.number().int(),
  /** How many of those would actually be written. The number to type back. */
  eligibleCount: z.number().int(),
  /**
   * The cap in force when this plan was made, so a refusal explains itself.
   *
   * Null: no per-run ceiling was in force — the shipped default. Stored as `0`
   * in a `NOT NULL` column for the same reason `capPerOlt` is; see
   * `toBulkPlanSummary`.
   */
  cap: z.number().int().nullable(),
  /** Null: no per-chassis limit was in force when this plan was made. */
  capPerOlt: z.number().int().nullable(),
  /** True when this plan is already too big to run. */
  overCap: z.boolean(),
  createdAt: z.number().int(),
  createdBy: z.string().nullable(),
  expiresAt: z.number().int(),
  executedAt: z.number().int().nullable(),
  executedBy: z.string().nullable(),
  /**
   * What the run did, COUNTED FROM THE TARGET ROWS rather than reported by the
   * process that ran it.
   *
   * These three used to be accumulated in memory and written once, after the
   * last target. A run that was abandoned halfway therefore recorded `0 / 0 /
   * 0` for forty subscribers it had already changed — the counters were only
   * ever correct if the loop reached its last line, which is a hope and not a
   * record. The per-target rows were right the whole time (each is written as
   * it finishes), so the counts are now derived from them on every read and
   * cannot disagree with the rows they count.
   */
  succeeded: z.number().int(),
  failed: z.number().int(),
  skipped: z.number().int(),
  /**
   * Targets the run never reached. Zero on a plan that finished.
   *
   * Non-zero means the work is half done: these subscribers still need the
   * change, and `POST /plans/:id/resume` is what plans the remainder.
   */
  abandoned: z.number().int(),
  /** Free text the execution leaves behind — the campaign id, for firmware. */
  note: z.string().nullable(),
  targets: z.array(bulkPlanTargetSchema),
  /**
   * Every refusal on this plan, gathered by reason. DERIVED from `targets`, on
   * the server, never stored — so it cannot drift from the rows it counts.
   */
  refusals: z.array(bulkRefusalGroupSchema),
});
export type BulkPlan = z.infer<typeof bulkPlanSchema>;

/** The list row: a plan without its targets, and so without their reasons. */
export const bulkPlanSummarySchema = bulkPlanSchema.omit({ targets: true, refusals: true });
export type BulkPlanSummary = z.infer<typeof bulkPlanSummarySchema>;

/**
 * The cap, and where it is raised.
 *
 * Both numbers are server-side and neither is negotiable by a client. The
 * defaults are the operator's standing rule — 1–2 registered test ONUs per
 * OLT — expressed as numbers rather than as a comment somebody has to read.
 */
/**
 * The values each limit may be set to.
 *
 * Enumerated rather than a range, and shared rather than duplicated in the
 * form, so the dropdown and the server agree by construction. A caller who
 * skips the form and PATCHes `7` for the per-chassis limit is refused by the
 * same list the operator picked from — the dropdown narrows what is easy, the
 * server decides what is possible.
 */
export const BULK_MAX_TARGETS_OPTIONS = [
  1, 2, 5, 10, 25, 50, 100, 250, 500, 1000, 2500,
] as const;
export const BULK_MAX_TARGETS_PER_OLT_OPTIONS = [
  50, 100, 150, 200, 250, 300, 350, 400, 450, 500, 1000, 2500,
] as const;

/**
 * "No per-chassis limit", as a value rather than as an absence.
 *
 * This distinction is the whole point. An ABSENT setting means "nobody has
 * chosen; use the shipped default", and a corrupt one must NOT mean the same
 * thing — see `BULK_SAFE_MAX_TARGETS`. `'none'` is a deliberate choice that
 * somebody made and that the audit trail records, so it can never be read as
 * either of the other two. The word is also what is stored, so a row saying
 * `none` is self-describing to anyone reading the settings table by hand.
 */
export const BULK_LIMIT_NONE = 'none';

/**
 * "Every ONU the plan matched" — the per-run limit's own word.
 *
 * A separate word from `'none'` rather than a shared one, because the two
 * fields answer different questions and a stored row has to say which it
 * answered. `none` means "there is no SECOND, per-chassis ceiling"; `all`
 * means "the run itself has no ceiling". They read differently in the settings
 * table months later, which is the only place either of them is ever read by
 * hand.
 *
 * This used to be impossible, and the comment here used to say it never would
 * be. It is the operator's decision, and it is a real change of posture: the
 * number is no longer what stands between this feature and the fleet. What
 * stands there now is `bulkConfirmation` — an exact count, the chassis's name,
 * and for a whole-chassis reboot or any recreate a word typed out in full. A
 * cap that stops an accident and a cap that stops the operator working are not
 * the same object, and only the first one is worth keeping.
 */
export const BULK_LIMIT_ALL = 'all';

export const bulkLimitsSchema = z.object({
  /** A number, or NULL meaning every ONU the plan matched. */
  maxTargets: z.number().int().nullable(),
  /** A number, or NULL meaning no separate per-chassis ceiling. */
  maxTargetsPerOlt: z.number().int().nullable(),
  /**
   * Where each value came from.
   *
   * Three words, not two. `unreadable` is the one that matters: a stored value
   * that is neither a sane number nor one of the two keywords does not fall
   * back to the shipped default any more, because the shipped default is now
   * "no limit" and falling back to it would let a garbled row authorise a write
   * to every subscriber on a chassis. It falls back to the SAFE value instead,
   * and says so here so the banner can explain why the limit is suddenly 2.
   */
  maxTargetsSource: z.enum(['default', 'operator', 'unreadable']),
  maxTargetsPerOltSource: z.enum(['default', 'operator', 'unreadable']),
  defaults: z.object({
    maxTargets: z.number().int().nullable(),
    maxTargetsPerOlt: z.number().int().nullable(),
  }),
  updatedAt: z.number().int().nullable(),
  updatedBy: z.string().nullable(),
});
export type BulkLimits = z.infer<typeof bulkLimitsSchema>;

/**
 * Changing a limit.
 *
 * Three states per field, and they are three different words on purpose:
 *   absent            — leave this one alone
 *   null              — restore the SHIPPED DEFAULT
 *   'all' / 'none'    — no limit at all, chosen deliberately
 *
 * "Back to the default" and "no limit" have to stay distinguishable even now
 * that the shipped default IS no limit, because they are still different acts:
 * one is a choice somebody made and the audit trail records with a reason, the
 * other is the absence of a choice. The day the default moves back down, a row
 * stored as `all` must keep meaning `all`.
 */
export const updateBulkLimitsSchema = z.object({
  maxTargets: z
    .union([
      z.literal(BULK_LIMIT_ALL),
      z.number().int().refine((n) => (BULK_MAX_TARGETS_OPTIONS as readonly number[]).includes(n), {
        message: `ONUs per run is one of ${BULK_MAX_TARGETS_OPTIONS.join(', ')}, or "${BULK_LIMIT_ALL}".`,
      }),
    ])
    .nullable()
    .optional(),
  maxTargetsPerOlt: z
    .union([
      z.literal(BULK_LIMIT_NONE),
      z
        .number()
        .int()
        .refine((n) => (BULK_MAX_TARGETS_PER_OLT_OPTIONS as readonly number[]).includes(n), {
          message: `The per-chassis limit is one of ${BULK_MAX_TARGETS_PER_OLT_OPTIONS.join(', ')}, or "${BULK_LIMIT_NONE}".`,
        }),
    ])
    .nullable()
    .optional(),
  /**
   * Why the boundary is moving. Required, and kept in the audit trail: raising
   * this is the single act that lets a bulk write leave the test ONUs, and
   * "who widened it and why" is the first question after an incident.
   */
  reason: z.string().trim().min(4).max(500),
});
export type UpdateBulkLimitsInput = z.infer<typeof updateBulkLimitsSchema>;

/**
 * The default, shipped: EVERY ONU the plan matched, on either count.
 *
 * This replaces the old 1–2 test ONUs per OLT, at the operator's instruction.
 * The test-ONU rule was expressed as a number because a sentence in a ledger
 * cannot refuse a request; it is now expressed as `bulkConfirmation` instead,
 * which can, and which scales with what is being asked rather than capping
 * everything at the size of the most dangerous thing.
 *
 * BOTH move together and that is not incidental. Leaving the per-chassis
 * default at 2 while the per-run default said "all" would advertise a limit
 * that does not exist and enforce one nobody chose — a run targets exactly one
 * chassis, so the smaller of the two is always what actually binds.
 */
export const BULK_DEFAULT_MAX_TARGETS: number | null = null;
export const BULK_DEFAULT_MAX_TARGETS_PER_OLT: number | null = null;

/**
 * Where an UNREADABLE stored value lands. Not the default, on purpose.
 *
 * The old code fell back to the shipped default on a corrupt row and explained
 * at length why the direction mattered: falling back refuses a run somebody
 * expected, which costs a minute, while reading a garbled row as "no limit"
 * authorises a write to every subscriber on a chassis. That argument is
 * untouched — what changed is that the default is now the unsafe direction, so
 * the fallback had to stop being the default and become this instead.
 *
 * 2 is the operator's own test-ONU number, which is the smallest thing that is
 * still useful and the value every install of this feature has had until now.
 */
export const BULK_SAFE_MAX_TARGETS = 2;
export const BULK_SAFE_MAX_TARGETS_PER_OLT = 2;

/** How long a plan stays executable. Long enough to read, short enough to be current. */
export const BULK_PLAN_TTL_MS = 15 * 60_000;
